import type { Item, ItemPatch } from '../../domain/inventory';
import { ApiError } from '../../api/errors';

export type ItemEditDraft = Partial<
  Pick<
    Item,
    | 'status'
    | 'needsAttention'
    | 'attentionReason'
    | 'hardwareRevision'
    | 'replacedByItemId'
    | 'notes'
    | 'location'
    | 'locationSiteId'
    | 'siteId'
    | 'inspectedDate'
    | 'inspectedBy'
    | 'maintenanceDate'
    | 'maintenanceBy'
    | 'maintenanceNotes'
  >
>;

export type ItemEditMode = 'edit' | 'clear-attention';

function hasOwnField<K extends keyof ItemEditDraft>(draft: ItemEditDraft, field: K): boolean {
  return Object.prototype.hasOwnProperty.call(draft, field);
}

export function initialItemEditDraft(mode: ItemEditMode): ItemEditDraft {
  return mode === 'clear-attention'
    ? {
        needsAttention: false,
        attentionReason: '',
      }
    : {};
}

/**
 * The backend validates needsAttention and attentionReason together even though
 * every other item field uses patch semantics. Whenever either attention field
 * changes, send the complete pair while leaving unrelated edits patch-only.
 */
export function buildItemEditPatch(item: Item, version: number, draft: ItemEditDraft): ItemPatch {
  const attentionChanged =
    hasOwnField(draft, 'needsAttention') || hasOwnField(draft, 'attentionReason');

  if (!attentionChanged) {
    return { id: item.id, version, ...draft };
  }

  const needsAttention = draft.needsAttention ?? item.needsAttention ?? false;
  const attentionReason = draft.attentionReason ?? item.attentionReason ?? '';

  return {
    id: item.id,
    version,
    ...draft,
    needsAttention,
    attentionReason: needsAttention ? attentionReason : '',
  };
}

export function itemEditErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.code === 'VALIDATION') {
    if (error.message.toLocaleLowerCase().includes('attention')) {
      return 'Review the attention flag and reason, then try again.';
    }
    if (error.message.toLocaleLowerCase().includes('replaced')) {
      return 'Enter the item ID of the device that replaced this one.';
    }
    return 'Review the item details and try again.';
  }
  return error instanceof Error ? error.message : 'The item could not be saved.';
}
